import React, { useState, useEffect, useMemo } from 'react';
import { Row, Col, Input, Button, Alert } from 'reactstrap';
import {
  HorariosAtencion as HorariosAtencionType,
  HorariosAtencionRegistrado,
} from 'types/Consultorio';

interface HorariosAtencionProps {
  id?: number;
  value?: HorariosAtencionType[];
  actuales?: HorariosAtencionRegistrado[];
  onChange?: (horarios: HorariosAtencionType[]) => void;
  disabled?: boolean;
}

const diasSemana = [
  'LUNES',
  'MARTES',
  'MIERCOLES',
  'JUEVES',
  'VIERNES',
  'SABADO',
  'DOMINGO',
];

const HorariosAtencion: React.FC<HorariosAtencionProps> = ({
  id=0,
  value = [],
  actuales = [],
  onChange,
  disabled = false,
}) => {
  const [horarios, setHorarios] = useState<HorariosAtencionType[]>(
    value.length > 0 ? value : [{ diaSemana: '', horaInicio: '', horaFin: '' }],
  );
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (id != 0 && value.length > 0) {
      setHorarios(value);
    }
  }, [value]);

  const handleHorarioChange = (index: number, field: keyof HorariosAtencionType, val: string) => {
    const nuevosHorarios = [...horarios];
    nuevosHorarios[index][field] = val;
    setHorarios(nuevosHorarios);
    setError('');
    onChange?.(nuevosHorarios);
  };

  const agregarHorario = () => {
    setHorarios([...horarios, { diaSemana: '', horaInicio: '', horaFin: '' }]);
  };

  const eliminarHorario = (index: number) => {
    const nuevosHorarios = horarios.filter((_, i) => i !== index);
    setHorarios(nuevosHorarios);
    onChange?.(nuevosHorarios);
  };

  // Agrupar horarios actuales por día de la semana
  const horariosAgrupados = useMemo(() => {
    const grupos: Record<string, HorariosAtencionRegistrado[]> = {};
    actuales.forEach(h => {
      if (!grupos[h.diaSemana]) grupos[h.diaSemana] = [];
      grupos[h.diaSemana].push(h);
    });
    return grupos;
  }, [actuales]);

  return (
    <div>
      {error && (
        <Alert color='danger' className='py-1 mb-3'>
          {error}
        </Alert>
      )}

      {horarios.map((horario, index) => (
        <Row key={index} className='align-items-center mb-2'>
          <Col sm={4}>
            <Input
              type='select'
              className="custom-select form-control"
              value={horario.diaSemana ?? ''}
              onChange={e => handleHorarioChange(index, 'diaSemana', e.target.value)}
              disabled={disabled}
              required
            >
              <option value="">Día...</option>
              {diasSemana.map(diaSemana => (
                <option key={diaSemana} value={diaSemana}>
                  {diaSemana}
                </option>
              ))}
            </Input>
          </Col>

          <Col sm={3}>
            <Input
              type='time'
              value={horario.horaInicio ?? ''}
              onChange={e => handleHorarioChange(index, 'horaInicio', e.target.value)}
              disabled={disabled}
              required
            />
          </Col>

          <Col sm={3}>
            <Input
              type='time'
              value={horario.horaFin ?? ''}
              onChange={e => handleHorarioChange(index, 'horaFin', e.target.value)}
              disabled={disabled}
              required
            />
          </Col>

          <Col sm={2} className='text-center'>
            <Button
              color='danger'
              type='button'
              onClick={() => eliminarHorario(index)}
              size='sm'
              disabled={disabled}
            >
              ✕
            </Button>
          </Col>
        </Row>
      ))}

      <Button
        color='secondary'
        type='button'
        className='mt-2'
        onClick={agregarHorario}
        disabled={disabled}
      >
        + Agregar horario
      </Button>

      {/* Sección informativa */}
      <div className="mt-4">
        <h6>Horarios ya registrados</h6>
        {actuales.length === 0 ? (
          <p className="text-muted mb-0">No hay horarios cargados actualmente.</p>
        ) : (
          <div className="border rounded p-2 bg-light">
            {Object.keys(horariosAgrupados).map(dia => (
              <div key={dia} className="mb-2">
                <strong>{dia}:</strong>
                <ul className="mb-0 pl-4">
                  {horariosAgrupados[dia].map((h, idx) => (
                    <li key={idx}>
                      {h.horaInicio} - {h.horaFin}
                      {h.consultorio_nombre ? ` (${h.consultorio_nombre})` : ''}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default HorariosAtencion;
